/**
 * Hook for Stripe Connect account status and actions.
 * Wraps create-connect-account and check-connect-status edge functions.
 */
import { useState, useCallback, useEffect } from 'react';
import { useApp } from '@/contexts/AppContext';
import { invokeAuthedFunction } from '@/lib/invokeAuthedFunction';
import { supabase } from '@/integrations/supabase/client';

export type ConnectStatus = 'not_connected' | 'onboarding_pending' | 'active';

export function useConnectAccount() {
  const { organization, refreshData } = useApp();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connectStatus: ConnectStatus = !organization?.stripe_connect_account_id
    ? 'not_connected'
    : organization.stripe_connect_charges_enabled
      ? 'active'
      : 'onboarding_pending';

  // Auto-check status on return from Stripe onboarding
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connect_return') === 'true' && organization?.stripe_connect_account_id) {
      checkStatus();
      // Clean up URL param
      params.delete('connect_return');
      const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.stripe_connect_account_id]);

  const startOnboarding = useCallback(async () => {
    if (!organization?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await invokeAuthedFunction<{ onboarding_url?: string; error?: string }>(
        supabase,
        'create-connect-account',
        { organization_id: organization.id }
      );
      if (data?.onboarding_url) {
        window.location.href = data.onboarding_url;
      } else {
        setError(data?.error || 'Failed to start onboarding');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start onboarding');
    } finally {
      setIsLoading(false);
    }
  }, [organization?.id]);

  const checkStatus = useCallback(async () => {
    if (!organization?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      await invokeAuthedFunction(supabase, 'check-connect-status', {
        organization_id: organization.id,
      });
      await refreshData();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to check status');
    } finally {
      setIsLoading(false);
    }
  }, [organization?.id, refreshData]);

  return {
    connectStatus,
    startOnboarding,
    checkStatus,
    isLoading,
    error,
    chargesEnabled: organization?.stripe_connect_charges_enabled ?? false,
    payoutsEnabled: organization?.stripe_connect_payouts_enabled ?? false,
    defaultPriceCents: organization?.default_customer_price_cents ?? null,
  };
}
